import { PATHS } from './paths';

export const SERVICE_CATEGORIES = [
  {
    value: 'VENUE',
    path: 'venues',
    labelId: 'app.search.venues',
  },
  {
    value: 'CATERER',
    path: 'caterers',
    labelId: 'app.search.caterers',
  },
  {
    value: 'DECORATOR',
    path: 'decorators',
    labelId: 'app.search.decorators',
  },
  {
    value: 'PHOTOGRAPHER',
    path: 'photographers',
    labelId: 'app.search.photographers',
  },
];

export const SEARCH_RESULT_PATHS = [
  PATHS.venues,
  PATHS.caterers,
  PATHS.decorators,
  PATHS.photographers,
];

export const isSearchResultsListPath = pathname =>
  SEARCH_RESULT_PATHS.includes(pathname);

export const isVendorDetailPath = pathname => {
  if (isSearchResultsListPath(pathname)) {
    return false;
  }
  return (
    pathname.startsWith(`${PATHS.venues}/`) ||
    pathname.startsWith(`${PATHS.caterers}/`) ||
    pathname.startsWith(`${PATHS.decorators}/`) ||
    pathname.startsWith(`${PATHS.photographers}/`)
  );
};

/** Results list + vendor detail: compact search UX (expand for full search + category tabs). */
export const usesCompactSearchBar = pathname =>
  isSearchResultsListPath(pathname) || isVendorDetailPath(pathname);

export const usesResultsPageSearchUx = usesCompactSearchBar;

export const getCategoryFromPath = pathname => {
  if (pathname.startsWith(PATHS.venues)) return 'VENUE';
  if (pathname.startsWith(PATHS.caterers)) return 'CATERER';
  if (pathname.startsWith(PATHS.decorators)) return 'DECORATOR';
  if (pathname.startsWith(PATHS.photographers)) return 'PHOTOGRAPHER';
  return null;
};

export const getPathForCategory = category => {
  const match = SERVICE_CATEGORIES.find(item => item.value === category);
  return match ? match.path : 'venues';
};

export const getCategoryOrDefault = storedCategory => {
  if (storedCategory) return storedCategory;
  return 'VENUE';
};

export const ACCOUNT_PATHS = [
  PATHS.favorite,
  PATHS.orderHistory,
  PATHS.profile,
];

export const HIDDEN_CATEGORY_NAV_PATHS = [
  PATHS.contactUs,
  PATHS.resetPassword,
  PATHS.verificationSuccess,
  PATHS.payment,
  PATHS.orderConfirmation,
  PATHS.orderDetail,
  PATHS.termsAndConditions,
  PATHS.privacyPolicy,
];

export const shouldShowSearchBar = pathname =>
  pathname === '/' || usesCompactSearchBar(pathname);

export const shouldShowCategoryNav = (pathname, isLoggedIn) => {
  if (HIDDEN_CATEGORY_NAV_PATHS.includes(pathname)) {
    return false;
  }
  if (!isLoggedIn) {
    return true;
  }
  return !ACCOUNT_PATHS.includes(pathname);
};

export const buildSearchQuery = ({ city, date, guests }) => {
  const params = { city };
  if (date) {
    params.date = date;
  }
  const guestCount = Number(guests);
  if (Number.isFinite(guestCount) && guestCount > 0) {
    params.guests = String(Math.floor(guestCount));
  }
  return Object.keys(params)
    .map(key => `${key}=${encodeURIComponent(params[key])}`)
    .join('&');
};

export function parseSearchGuestsParam(rawGuests) {
  const value = Array.isArray(rawGuests) ? rawGuests[0] : rawGuests;
  const guestCount = Number(value);
  if (!Number.isFinite(guestCount) || guestCount <= 0) {
    return null;
  }
  return Math.floor(guestCount);
}

/** Normalize search/event date to `YYYY-MM-DD`, or null if invalid. */
export function parseSearchDateParam(rawDate) {
  const value = Array.isArray(rawDate) ? rawDate[0] : rawDate;
  if (value == null || value === '') {
    return null;
  }
  const text = String(value).trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return null;
  }
  const [year, month, day] = text.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return text;
}

/**
 * Append booking-relevant search params (`guests`, `date`) to a vendor detail path.
 * @param {string} path
 * @param {{ guests?: unknown, date?: unknown }} [params]
 */
export function withVendorDetailSearchParams(path, { guests, date } = {}) {
  if (!path) {
    return path;
  }
  const [pathname, existingQuery = ''] = String(path).split('?');
  const params = new URLSearchParams(existingQuery);
  const guestCount = parseSearchGuestsParam(guests);
  if (guestCount != null) {
    params.set('guests', String(guestCount));
  }
  const eventDate = parseSearchDateParam(date);
  if (eventDate != null) {
    params.set('date', eventDate);
  }
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}

/** @deprecated use withVendorDetailSearchParams */
export function withGuestsQuery(path, guests) {
  return withVendorDetailSearchParams(path, { guests });
}

/** Digits only; no leading zeros; empty while clearing. Min valid value when non-empty is 1. */
export function sanitizeSearchGuestsInput(raw) {
  if (raw == null || raw === '') {
    return '';
  }
  const digits = String(raw).replace(/\D/g, '');
  if (!digits) {
    return '';
  }
  return digits.replace(/^0+/, '');
}
